import apiClient from './apiClient';
import { db } from './firebase';
import { collection, addDoc, getDocs, updateDoc, doc, getDoc, onSnapshot, query, where, writeBatch, Timestamp } from 'firebase/firestore';
import { createTransaction } from './transactionService';
import { createNotification } from './notificationService';

const accountsCollection = collection(db, 'accounts');

export const createAccount = async (account, currentUser) => {
    const userRef = doc(db, 'users', currentUser.uid);
    const { license } = currentUser;

    if (!currentUser.enabled) {
        throw new Error('No puedes crear cuentas porque tu cuenta aún no ha sido habilitada.');
    }
    // Verificar si el usuario ha alcanzado el límite de cuentas
    if (license.accountsCreated >= license.accountLimit) {
        throw new Error('Has alcanzado el límite de cuentas permitidas para tu licencia.');
    }

    const newAccount = { ...account }; // Crear una copia del objeto account
    newAccount.ownerUid = currentUser.uid;
    const accountDocRef = await addDoc(accountsCollection, newAccount);
    // Actualizar el contador de cuentas creadas en la licencia del usuario

    await updateDoc(userRef, {
        'license.accountsCreated': license.accountsCreated + 1,
    });

    const transaction = {
        createdAt: new Date(),
        accountId: accountDocRef.id,
        categoryId: null,
        amount: parseFloat(account.initialBalance),
        type: 'initial',
        date: new Date(),
        description: 'Initial balance',
        ownerUid: account.ownerUid
    };
    await createTransaction(transaction, currentUser, true);

    // console.log('Account created with ID:', accountDocRef.id);
    return accountDocRef;
};

export const getAccount = async (id) => {
    const accountDoc = doc(db, 'accounts', id);
    const accountSnapshot = await getDoc(accountDoc);
    if (accountSnapshot.exists()) {
        return { id: accountSnapshot.id, ...accountSnapshot.data() };
    } else {
        console.error('Account not found');
        throw new Error('Account not found');
    }
};

export const updateGroupCollaborators = async (groupId, data) => {
    const groupRef = doc(db, 'groups', groupId);
    const groupSnapshot = await getDoc(groupRef);

    if (!groupSnapshot.exists()) {
        throw new Error('El grupo no existe.');
    }

    const groupData = groupSnapshot.data();

    // 1. Actualizar el grupo principal
    await updateDoc(groupRef, { collaborators: data.collaborators, collaboratorsUid: data.collaboratorsUid });

    // 2. Propagar los cambios a todas las cuentas asociadas al grupo
    if (groupData.accounts && Array.isArray(groupData.accounts)) {
        const updatePromises = groupData.accounts.map(async (accountId) => {
            const accountRef = doc(db, 'accounts', accountId);
            // Sincronizar los colaboradores de la cuenta con los del grupo
            await updateDoc(accountRef, {
                collaborators: data.collaborators,
                collaboratorsUid: data.collaboratorsUid
            });
        });

        await Promise.all(updatePromises);
    }
};

export const updateAccountCollaborators = async (accountId, data, newCollaborator) => {
    try {
        const accountRef = doc(db, 'accounts', accountId);

        // Consultar la cuenta para obtener su nombre
        const accountSnapshot = await getDoc(accountRef);
        if (!accountSnapshot.exists()) {
            throw new Error('La cuenta asociada no existe.');
        }

        const accountData = accountSnapshot.data();
        const accountName = accountData.name || 'Cuenta sin nombre'; // Fallback si no hay nombre

        // Actualizar los colaboradores de la cuenta
        await updateDoc(accountRef, { collaborators: data.collaborators, collaboratorsUid: data.collaboratorsUid });

        // Crear notificación solo para el nuevo colaborador
        if (newCollaborator) {
            await createNotification({
                read: false,
                recipientUid: newCollaborator.uid,
                title: 'Te han agregado como colaborador',
                body: `Has sido agregado como colaborador en la cuenta "${accountName}".`,
                type: 'collaboration',
                accountId,
            });
        }
    } catch (error) {
        console.error('Error al actualizar los colaboradores:', error);
        throw error;
    }
};

export const updateDailyAmountsForAccount = async (accountId, datedailyAmounts) => {
    try {
        const accountRef = doc(db, 'accounts', accountId);
        await updateDoc(accountRef, { datedailyAmounts });
    } catch (error) {
        console.error('Error al actualizar los montos diarios:', error);
        throw error;
    }
};

export const updateDailyAmountsForGroup = async (groupId, datedailyAmounts) => {
    const groupRef = doc(db, 'groups', groupId);
    await updateDoc(groupRef, { datedailyAmounts });
};

export const updateAccount = async (selectedAccount, account) => {
    const id = selectedAccount.id;
    const accountDoc = doc(db, 'accounts', id);

    // Update the account document
    await updateDoc(accountDoc, account);

    // Update the initial transaction
    const transactionsCollection = collection(db, 'transactions');
    const q = query(transactionsCollection, where('accountId', '==', id), where('type', '==', 'initial'));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
        const initialTransactionDoc = querySnapshot.docs[0];
        await updateDoc(initialTransactionDoc.ref, {
            amount: parseFloat(account.initialBalance),
            description: 'Updated initial balance'
        });
    }

    return accountDoc;
};

export const deleteAccount = async (account) => {
    // Eliminar las transacciones asociadas a la cuenta
    const transactionsCollection = collection(db, 'transactions');
    const q = query(transactionsCollection, where('accountId', '==', account.id));
    const querySnapshot = await getDocs(q);

    const batch = writeBatch(db);

    querySnapshot.forEach((doc) => {
        console.log('Deleting transaction with ID:', doc.id);
        batch.delete(doc.ref);
    });

    batch.delete(doc(db, 'accounts', account.id));

    await batch.commit();
    console.log('Account and related transactions deleted');
};

export const onAccountsSnapshot = (uid, callback) => {
    const q = query(accountsCollection, where('collaboratorsUid', 'array-contains', uid));

    const handleSnapshot = async (snapshot) => {
        const accounts = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
        return accounts;
    };

    const unsubscribeUserAccounts = onSnapshot(q, async (snapshot) => {
        const userAccounts = await handleSnapshot(snapshot);
        callback(userAccounts);
    }, (error) => {
        console.error("Error fetching user accounts snapshot: ", error);
        callback([]);
    });

    return () => {
        // console.log('Unsubscribing from accounts snapshot');
        unsubscribeUserAccounts();
    };
};

export const getCollaborativeAccounts = async (collaborativeAccounts) => {
    const accounts = [];
    for (const account of collaborativeAccounts) {
        const accountDoc = doc(db, 'accounts', account.accountId);
        const accountSnapshot = await getDoc(accountDoc);
        if (accountSnapshot.exists()) {
            const accountData = { id: accountSnapshot.id, ...accountSnapshot.data() };
            accounts.push(accountData);
        }
    }
    return accounts;
};

export const getUserIdByEmail = async (email) => {
    console.log('Fetching user ID by email:', email);
    const q = query(collection(db, 'users'), where('email', '==', email));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
        console.log('User ID found:', querySnapshot.docs[0].id);
        return querySnapshot.docs[0].id;
    }
    console.error('User not found');
    throw new Error('User not found');
};

export const updateUser = async (userId, data) => {
    console.log('Updating user with ID:', userId);
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, data);
    console.log('User updated');
};

export const getAccountById = async (id) => {
    if (!id) {
        console.error('Invalid account ID');
    }

    const accountDoc = doc(db, 'accounts', id);
    const accountSnapshot = await getDoc(accountDoc);
    if (accountSnapshot.exists()) {
        return { id: accountSnapshot.id, ...accountSnapshot.data() };
    } else {
        console.error('Account not found');
        throw new Error('Account not found');
    }
};

/**
 * Archivar una cuenta (soft delete)
 * La cuenta se marca como archivada pero no se elimina de la base de datos
 */
export const archiveAccount = async (account, currentUser, reason = '') => {
    if (!currentUser.enabled) {
        throw new Error('No tienes permisos para archivar cuentas.');
    }

    const accountRef = doc(db, 'accounts', account.id);

    const archiveData = {
        archived: true,
        archivedAt: Timestamp.now(),
        archivedBy: currentUser.uid,
        archivedReason: reason
    };

    // Actualizar en Firestore
    await updateDoc(accountRef, archiveData);

    // Actualizar en MySQL
    try {
        await apiClient.put('/accounts.php', {
            id: account.id,
            archived: 1,
            archived_at: new Date().toISOString(),
            archived_by: currentUser.uid,
            archived_reason: reason
        });
    } catch (error) {
        console.error('Error archivando cuenta en MySQL:', error);
        // No lanzamos error para que al menos se archive en Firestore
    }

    console.log('Account archived:', account.id);
    return archiveData;
};

/**
 * Desarchivar una cuenta
 * Reactiva una cuenta previamente archivada
 */
export const unarchiveAccount = async (account, currentUser) => {
    if (!currentUser.enabled) {
        throw new Error('No tienes permisos para desarchivar cuentas.');
    }

    const accountRef = doc(db, 'accounts', account.id);

    const unarchiveData = {
        archived: false,
        archivedAt: null,
        archivedBy: null,
        archivedReason: null
    };

    // Actualizar en Firestore
    await updateDoc(accountRef, unarchiveData);

    // Actualizar en MySQL
    try {
        await apiClient.put('/accounts.php', {
            id: account.id,
            archived: 0,
            archived_at: null,
            archived_by: null,
            archived_reason: null
        });
    } catch (error) {
        console.error('Error desarchivando cuenta en MySQL:', error);
        // No lanzamos error para que al menos se desarchive en Firestore
    }

    console.log('Account unarchived:', account.id);
    return unarchiveData;
};
// ... existing exports ...

/**
 * Realiza el cierre de año para una cuenta.
 * 1. Calcula el impacto de todas las transacciones hasta el 31 de dic del año dado.
 * 2. Actualiza el balance inicial de la cuenta con ese total.
 * 3. Archiva las transacciones de ese año.
 */
export const performYearEndClose = async (account, year, currentUser) => {
    console.log(`Iniciando cierre de año ${year} para cuenta ${account.name}`);

    // Verificar si la cuenta ya fue cerrada para este año
    if (account.lastYearClosed === year) {
        throw new Error(`La cuenta "${account.name}" ya fue cerrada para el año ${year}.`);
    }

    // 1. Fechas límite
    const endDate = new Date(`${year}-12-31T23:59:59.999`);
    const endTimestamp = Timestamp.fromDate(endDate);

    // 2. Calcular el SALDO ACTUAL de la cuenta (todas las transacciones, no solo hasta el año de cierre)
    const transactionsRef = collection(db, 'transactions');

    // Obtener saldo actual desde la API (que ya lo calcula correctamente)
    let currentBalance = 0;
    try {
        const response = await apiClient.get('/procedures.php', {
            params: { accountIds: account.id, isGroup: 0 }
        });
        const data = response.data.data || [];
        if (data.length > 0 && data[0].balance_total !== undefined) {
            currentBalance = parseFloat(data[0].balance_total);
        } else {
            currentBalance = parseFloat(account.initialBalance || 0);
        }
    } catch (error) {
        console.error(`Error obteniendo saldo para ${account.name}:`, error);
        currentBalance = parseFloat(account.initialBalance || 0);
    }

    console.log(`Saldo ACTUAL calculado para ${account.name}: ${currentBalance}`);

    // 3. Obtener transacciones DEL AÑO a cerrar (solo para archivar)
    const qYear = query(
        transactionsRef,
        where('accountId', '==', account.id),
        where('date', '<=', endTimestamp)
    );
    const snapshotYear = await getDocs(qYear);
    const transactionsToArchive = snapshotYear.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(tx => tx.archived !== true);

    // Transferencias entrantes del año a cerrar
    const qYearDest = query(
        transactionsRef,
        where('destinationAccountId', '==', account.id)
    );
    const snapshotYearDest = await getDocs(qYearDest);
    const incomingToArchive = snapshotYearDest.docs.filter(doc => {
        const tx = doc.data();
        if (tx.archived === true) return false;
        // Filtrar por fecha: solo las del año a cerrar
        if (tx.date && tx.date.seconds) {
            const txDate = new Date(tx.date.seconds * 1000);
            return txDate <= endDate;
        }
        return false;
    });

    console.log(`Transacciones a archivar: ${transactionsToArchive.length} salientes, ${incomingToArchive.length} entrantes`);

    // Si no hay transacciones para archivar, solo actualizar balance
    if (transactionsToArchive.length === 0 && incomingToArchive.length === 0) {
        console.log(`Cuenta ${account.name} no tiene transacciones del año ${year} para archivar.`);

        const accountRef = doc(db, 'accounts', account.id);
        await updateDoc(accountRef, {
            initialBalance: currentBalance,
            lastYearClosed: year
        });

        await apiClient.put('/accounts.php', {
            id: account.id,
            initialBalance: currentBalance,
            lastYearClosed: year
        });

        return currentBalance;
    }

    console.log(`Nuevo Balance Inicial: ${currentBalance}`);

    const batch = writeBatch(db);

    // 4. Actualizar Account (Firestore)
    const accountRef = doc(db, 'accounts', account.id);
    batch.update(accountRef, {
        initialBalance: currentBalance,
        lastYearClosed: year
    });

    // 5. Archivar transacciones (Firestore) - Batch (limite 500, hay que iterar si son más)
    // Para simplificar ejemplo asumimos < 500 o podemos hacerlo en chunks

    // Archivar transacciones del año
    snapshotYear.docs.forEach(doc => {
        batch.update(doc.ref, {
            archived: true,
            archivedReason: `Cierre año ${year}`
        });
    });
    // Archivar transferencias entrantes del año
    incomingToArchive.forEach(doc => {
        batch.update(doc.ref, {
            archived: true,
            archivedReason: `Cierre año ${year}`
        });
    });

    // Ejecutar Batch Firestore
    await batch.commit();

    // 6. Actualizar MySQL (API)
    // (Actualizar cuenta)
    await apiClient.put('/accounts.php', {
        id: account.id,
        initialBalance: currentBalance,
        lastYearClosed: year
    });

    // (Archivar transacciones en MySQL)
    // Esto idealmente sería un endpoint batch, pero por simplicidad
    // podemos llamar a un endpoint especial '/archive-transactions'
    // O iterar (lento). 
    // Vamos a asumir que creemos un endpoint nuevo o usamos un loop para este MVP.
    // O mejor: enviamos el rango de fechas al backend para que archive todo.

    await apiClient.post('/archive-transactions.php', {
        accountId: account.id,
        endDate: `${year}-12-31`,
        reason: `Cierre año ${year}`
    });

    return currentBalance;
};

/**
 * Realiza el cierre de año para TODAS las cuentas del sistema.
 * Solo debe ser ejecutado por el rootUser.
 * @param {number} year - Año a cerrar (ej: 2024)
 * @param {object} currentUser - Usuario actual (debe ser rootUser)
 * @returns {object} Resumen de resultados { success: [], failed: [] }
 */
export const performGlobalYearEndClose = async (year, currentUser) => {
    if (currentUser.role !== 'rootUser') {
        throw new Error('Solo el usuario root puede ejecutar el cierre global de año.');
    }

    console.log(`Iniciando cierre global de año ${year} para TODAS las cuentas del sistema`);

    // 1. Obtener TODAS las cuentas del sistema (sin filtro de ownerUid)
    const accountsRef = collection(db, 'accounts');
    const snapshot = await getDocs(accountsRef);

    if (snapshot.empty) {
        throw new Error('No se encontraron cuentas para cerrar.');
    }

    const accounts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log(`Se encontraron ${accounts.length} cuentas en el sistema para cerrar.`);

    // 2. Ejecutar cierre para cada cuenta
    const results = {
        success: [],
        failed: []
    };

    for (const account of accounts) {
        try {
            // Permitir re-cerrar cuentas para recalcular balances correctos
            if (account.lastYearClosed === year) {
                console.log(`Cuenta ${account.name} ya fue cerrada para el año ${year}. Recalculando balance...`);
            }

            const newBalance = await performYearEndClose(account, year, currentUser);
            results.success.push({
                accountId: account.id,
                accountName: account.name,
                newBalance
            });
            console.log(`✓ Cuenta ${account.name} cerrada exitosamente. Nuevo balance: ${newBalance}`);
        } catch (error) {
            console.error(`✗ Error al cerrar cuenta ${account.name}:`, error);
            results.failed.push({
                accountId: account.id,
                accountName: account.name,
                error: error.message
            });
        }
    }

    console.log('Cierre global completado:', results);
    return results;
};

